import { useCharles } from "@/contexts/CharlesContext";
import { CharlesErrorState } from "@/components/employee/CharlesErrorStates";
import { Loader2, Mic, MicOff, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";
import { emitConciergeEvent } from "@/lib/concierge/events";
import { ConciergeChat } from "./ConciergeChat";

export function ConciergeAdvisor({
  advisorName,
  siteId,
}: {
  advisorName?: string | null;
  siteId?: string;
}) {
  const {
    session,
    conversation,
    configError,
    started,
    forceTextOnly,
    micDenied,
    voiceOptIn,
    start,
    stop,
    sendText,
    micMuted,
    setMicMuted,
  } = useCharles();
  const [text, setText] = useState("");
  const sessionStartRef = useRef<number | null>(null);

  const isConnectedNow = conversation.status === "connected";

  useEffect(() => {
    if (!siteId) return;
    if (isConnectedNow && sessionStartRef.current == null) {
      sessionStartRef.current = Date.now();
      emitConciergeEvent(siteId, "session_start");
    } else if (!isConnectedNow && sessionStartRef.current != null) {
      const duration_ms = Date.now() - sessionStartRef.current;
      sessionStartRef.current = null;
      emitConciergeEvent(siteId, "session_end", { metadata: { duration_ms } });
    }
  }, [isConnectedNow, siteId]);


  if (configError) return <CharlesErrorState code="config_error" />;
  if (session.status === "loading") {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-5 w-5 animate-spin opacity-60" />
      </div>
    );
  }
  if (session.status === "error") return <CharlesErrorState code={session.code} />;

  const showVoiceUI = voiceOptIn && !forceTextOnly;
  const isConnected = conversation.status === "connected";
  const isSpeaking = conversation.isSpeaking;
  const name = advisorName?.trim() || "your advisor";
  const statusLabel = !started
    ? showVoiceUI
      ? `Tap the sphere to speak with ${name}`
      : `Tap the sphere to chat with ${name}`
    : isConnected
      ? isSpeaking
        ? `${name} is speaking…`
        : micMuted
          ? "Mic muted — type or unmute"
          : "Listening"
      : "Connecting…";

  const handleSend = () => {
    const t = text.trim();
    if (!t) return;
    sendText(t);
    setText("");
  };

  return (
    <div className="flex flex-col items-center gap-5 py-8">
      <button
        onClick={() => { if (!started) start(); }}
        aria-label={started ? "Advisor active" : `Start conversation with ${name}`}
        className={cn(
          "relative rounded-full transition-transform duration-300",
          !started && "hover:scale-[1.03] active:scale-[0.98] cursor-pointer",
          started && "cursor-default",
        )}
        style={{
          height: "min(38vw, 168px)",
          width: "min(38vw, 168px)",
          animation: "concierge-orb-breathe 4s ease-in-out infinite",
        }}
      >
        <span
          aria-hidden
          className="absolute -inset-8 rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(255,138,61,0.14) 0%, rgba(255,138,61,0) 70%)",
            opacity: isConnected ? 0 : 1,
            transition: "opacity 700ms ease",
          }}
        />
        <span
          aria-hidden
          className="absolute -inset-8 rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(52,211,153,0.22) 0%, rgba(52,211,153,0) 70%)",
            opacity: isConnected ? 1 : 0,
            transition: "opacity 700ms ease",
          }}
        />
        <span
          aria-hidden
          className="absolute inset-0 rounded-full"
          style={{
            background:
              "radial-gradient(circle at 30% 28%, hsl(var(--orb-stop-1)) 0%, hsl(var(--orb-stop-2)) 38%, hsl(var(--orb-stop-3)) 75%, hsl(var(--orb-stop-4)) 100%)",
            boxShadow:
              "0 18px 44px -18px hsl(var(--orb-stop-3) / 0.35), inset -8px -12px 24px rgba(0,0,0,0.32)",
          }}
        />
        <span
          aria-hidden
          className="absolute inset-0 rounded-full"
          style={{
            background:
              "radial-gradient(circle at 30% 28%, hsl(var(--orb-stop-1-alt)) 0%, hsl(var(--orb-stop-2-alt)) 38%, hsl(var(--orb-stop-3-alt)) 75%, hsl(var(--orb-stop-4-alt)) 100%)",
            boxShadow:
              "0 18px 44px -18px hsl(var(--orb-stop-3-alt) / 0.42), 0 0 28px 4px hsl(var(--orb-stop-2-alt) / 0.35), inset -8px -12px 24px rgba(0,0,0,0.32)",
            opacity: isConnected ? 1 : 0,
            transition: "opacity 900ms ease",
          }}
        />
        {isSpeaking && (
          <span
            aria-hidden
            className="absolute inset-0 rounded-full"
            style={{
              boxShadow: isConnected
                ? "0 0 0 2px rgba(52,211,153,0.25)"
                : "0 0 0 2px rgba(255,138,61,0.25)",
              animation: "concierge-orb-ring 1.6s ease-out infinite",
            }}
          />
        )}
        <span
          aria-hidden
          className="absolute rounded-full opacity-70"
          style={{
            top: "16%",
            left: "22%",
            height: "12%",
            width: "12%",
            background: "rgba(255,255,255,0.85)",
            filter: "blur(3px)",
          }}
        />
      </button>

      <p className="text-sm text-muted-foreground text-center min-h-[1.25rem]">
        {statusLabel}
      </p>

      {micDenied && (
        <p className="max-w-sm text-xs text-muted-foreground text-center border border-border rounded-md px-3 py-2">
          Mic access denied — using text chat instead.
        </p>
      )}

      {/* Toolbar: mic mute (only while voice session is live) */}
      {started && showVoiceUI && (
        <div className="flex items-center gap-2">
          <Button
            variant={micMuted ? "default" : "outline"}
            size="sm"
            onClick={() => setMicMuted(!micMuted)}
            className="gap-2"
          >
            {micMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            {micMuted ? "Unmute mic" : "Mute mic"}
          </Button>
          <Button variant="ghost" size="sm" onClick={stop} className="text-muted-foreground">
            End conversation
          </Button>
        </div>
      )}

      {started && !showVoiceUI && (
        <Button variant="ghost" onClick={stop} className="text-muted-foreground">
          End conversation
        </Button>
      )}

      {/* Transcript */}
      {started && (
        <div className="w-full max-w-2xl space-y-3">
          <ConciergeChat advisorName={advisorName} />

          {/* Composer — available in both voice and text-only modes */}
          <div className="flex gap-2">
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type to your advisor…"
              disabled={!isConnected}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
            <Button onClick={handleSend} disabled={!isConnected || !text.trim()}>
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ConciergeAdvisor;
